import { assignTrendFills } from './assign-trend-fills.js';

/**
 * A governor seat: its party now, and whether it's up this year
 * @typedef {import('./assign-trend-fills.js').Seat & { party: string, contested: boolean }} GovernorSeat
 */

// Position winner fills independently of the original party outlines.
/**
 * @param {GovernorSeat[]} seats
 * @param {import('../types').ChamberTrends | undefined} report the trends feed's governor numbers
 */
export function assignGovernorFills(seats, report) {
  const parties = /** @type {const} */ (['dem', 'gop', 'others']);

  const won = Object.fromEntries(
    parties.map((party) => [party, Number(report?.[party]?.won ?? 0)])
  );

  const net = Object.fromEntries(
    parties.map((party) => [
      party,
      Number(report?.[party]?.netWinners ?? 0),
    ])
  );

  const contested = seats.filter((seat) => seat.contested);

  const totalWon = parties.reduce((sum, party) => sum + won[party], 0);

  if (
    parties.some((party) => !Number.isInteger(won[party]) || won[party] < 0) ||
    totalWon > contested.length
  ) {
    throw new Error('Governor winner counts do not fit the seat layout.');
  }

  if (
    parties.some((party) => !Number.isInteger(net[party])) ||
    parties.reduce((sum, party) => sum + net[party], 0) !== 0
  ) {
    throw new Error('Governor netWinners must be integers and sum to zero.');
  }

  const originalFills = new Map(
    seats.map((seat) => [seat.n, seat.contested ? 'undecided' : seat.party])
  );

  // Supply winner counts to the shared allocator.
  // The allocator determines their visual positions.
  const winnerColors = [
    ...Array(won.dem).fill('dem'),
    ...Array(won.gop).fill('gop'),
    ...Array(won.others).fill('other'),
  ];

  contested.forEach((seat, index) => {
    originalFills.set(seat.n, winnerColors[index] ?? 'undecided');
  });

  const byNumber = new Map(seats.map((seat) => [seat.n, seat]));

  return assignTrendFills(
    seats,
    (n) => originalFills.get(n),
    (n) => !byNumber.get(n)?.contested,
    (n) => `is_${byNumber.get(n)?.party}`,
    {
      dem: net.dem,
      gop: net.gop,
    },
    277.7,
    36.943
  );
}
